/* Vreme listića -> UTC dani aplikacije.

   Listić beleži lokalno vreme (Europe/Belgrade): CET = UTC+1, leti CEST = UTC+2 (od poslednje nedelje marta 02:00
   do poslednje nedelje oktobra 03:00 po lokalnom vremenu). Aktivnosti se čuvaju u UTC, kao sa kartice.

   Računa se u "naivnim" milisekundama (zidno vreme zapisano kao Date.UTC), bez Date aritmetike preko zona pregledača:
   tačno 24 h je uvek 86 400 000 ms. U Date objekat aplikacije (zidno vreme) se pretvara tek na kraju (toAppDate). */

export const MIN_MS = 60000;
export const DAY_MS = 86400000;

/** naivni ms zidnog vremena za datum (Date aplikacije, uzima se dan) + minuti */
export const naive = (date, minutes = 0) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) + minutes * MIN_MS;

/** naivni ms -> Date aplikacije (zidno vreme) */
export const toAppDate = (ms) => {
    const d = new Date(ms);
    return new Date(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), 0, 0);
};

/** početak dana (naivni ms) */
export const dayOf = (ms) => Math.floor(ms / DAY_MS) * DAY_MS;

/** poslednja nedelja meseca (naivni ms ponoći) */
const lastSunday = (year, month) => {
    const last = Date.UTC(year, month + 1, 0);
    return last - new Date(last).getUTCDay() * DAY_MS;
};

/** pomeraj Beograda u ms za lokalno (zidno) vreme; dvosmislen sat na kraju leta računa se kao letnji */
export function beogradOffset(localMs) {
    const y = new Date(localMs).getUTCFullYear();
    const start = lastSunday(y, 2) + 2 * 3600000; // 02:00 lokalno -> 03:00
    const end = lastSunday(y, 9) + 3 * 3600000; // 03:00 lokalno -> 02:00
    return localMs >= start && localMs < end ? 2 * 3600000 : 3600000;
}

export const localToUtc = (localMs) => localMs - beogradOffset(localMs);

/**
 * Segmenti sa skale listića -> apsolutni lokalni i UTC intervali.
 * @param segments [{start, end, type}] minuti skale (type null = listić nije bio uložen)
 * @param datumOd  Date — datum ulaganja (upisan na listiću)
 * @param ulaganje minut ulaganja na skali; sve pre njega na skali pripada sledećem danu
 * @returns {{ lokalno: {od, do}, utc: {od, do}, intervali: [{type, od, do}] (UTC naivni ms, bez "nema") }}
 */
export function listicUIntervale(segments, datumOd, ulaganje) {
    const base = naive(datumOd);
    const abs = (m) => base + (m >= ulaganje ? m : m + 1440) * MIN_MS;
    const parts = [];
    segments.forEach((s) => {
        if (!s.type) return;
        /* segment koji prelazi tačku ulaganja deli se na dva dela */
        if (s.start < ulaganje && s.end > ulaganje) {
            parts.push({ type: s.type, a: ulaganje, b: s.end });
            parts.push({ type: s.type, a: s.start, b: ulaganje });
        } else parts.push({ type: s.type, a: s.start, b: s.end });
    });
    const lokal = parts.map((p) => {
        const od = abs(p.a);
        const kraj = p.b === ulaganje && p.a < ulaganje ? base + (ulaganje + 1440) * MIN_MS : od + (p.b - p.a) * MIN_MS;
        return { type: p.type, od, do: kraj };
    }).filter((x) => x.do > x.od).sort((x, y) => x.od - y.od);
    /* spoji susedne iste tipove */
    const spojeno = [];
    lokal.forEach((x) => {
        const last = spojeno[spojeno.length - 1];
        if (last && last.type === x.type && last.do === x.od) last.do = x.do;
        else spojeno.push({ ...x });
    });
    if (!spojeno.length) return { lokalno: null, utc: null, intervali: [] };
    const lOd = spojeno[0].od, lDo = spojeno[spojeno.length - 1].do;
    const intervali = spojeno.map((x) => ({ type: x.type, od: localToUtc(x.od), do: localToUtc(x.do) })).filter((x) => x.do > x.od);
    return { lokalno: { od: lOd, do: lDo }, utc: { od: localToUtc(lOd), do: localToUtc(lDo) }, intervali };
}

/** UTC intervali -> po danima: [{dan (naivni ms), od, do (pokrivenost tog dana), intervali}] */
export function poDanima(intervali, pokrivenost) {
    if (!pokrivenost) return [];
    const dani = [];
    for (let d = dayOf(pokrivenost.od); d < pokrivenost.do; d += DAY_MS) {
        const od = Math.max(d, pokrivenost.od), kraj = Math.min(d + DAY_MS, pokrivenost.do);
        if (kraj <= od) continue;
        const delovi = intervali
            .map((x) => ({ type: x.type, od: Math.max(x.od, od), do: Math.min(x.do, kraj) }))
            .filter((x) => x.do > x.od);
        dani.push({ dan: d, od, do: kraj, intervali: delovi });
    }
    return dani;
}

const BREAK = 'break/rest';

/**
 * Spajanje dana listića sa postojećim danom.
 * @param dan       { dan, od, do, intervali } (naivni ms)
 * @param postojeci detalji postojećeg dana [{activityType, startTime, endTime}] (Date aplikacije) ili null
 * @returns {{ detalji: [{activityType, od, do}] (pun dan, naivni ms), konflikt: [{activityType, od, do}] }}
 *          konflikt = postojeće aktivnosti osim odmora koje se preklapaju sa pokrivenošću listića
 */
export function spojiDan(dan, postojeci) {
    const start = dan.dan, end = dan.dan + DAY_MS;
    const stari = (postojeci || []).map((d) => ({
        activityType: d.activityType,
        od: naive(new Date(d.startTime), new Date(d.startTime).getHours() * 60 + new Date(d.startTime).getMinutes()),
        do: naive(new Date(d.endTime), new Date(d.endTime).getHours() * 60 + new Date(d.endTime).getMinutes()),
    })).filter((d) => d.do > d.od);
    const konflikt = stari.filter((d) => d.activityType !== BREAK && d.od < dan.do && d.do > dan.od);

    /* postojeće van pokrivenosti listića ostaje; unutar pokrivenosti važi listić */
    const out = [];
    stari.forEach((d) => {
        if (d.od < dan.od) out.push({ ...d, do: Math.min(d.do, dan.od) });
        if (d.do > dan.do) out.push({ ...d, od: Math.max(d.od, dan.do) });
    });
    dan.intervali.forEach((x) => out.push({ activityType: x.type, od: x.od, do: x.do }));
    out.sort((a, b) => a.od - b.od);

    /* rupe -> odmor, spoji susedne iste tipove */
    const pun = [];
    let t = start;
    out.forEach((d) => {
        const od = Math.max(d.od, t);
        if (d.do <= od) return;
        if (od > t) pun.push({ activityType: BREAK, od: t, do: od });
        pun.push({ activityType: d.activityType, od, do: d.do });
        t = d.do;
    });
    if (t < end) pun.push({ activityType: BREAK, od: t, do: end });
    const detalji = [];
    pun.forEach((d) => {
        const last = detalji[detalji.length - 1];
        if (last && last.activityType === d.activityType && last.do === d.od) last.do = d.do;
        else detalji.push({ ...d });
    });
    return { detalji, konflikt };
}

/** hh:mm za minute skale */
export const fmtMin = (m) => {
    const x = ((Math.round(m) % 1440) + 1440) % 1440;
    return `${String(Math.floor(x / 60)).padStart(2, '0')}:${String(x % 60).padStart(2, '0')}`;
};

/** dd.mm.gggg hh:mm za naivni ms */
export const fmtNaive = (ms, withTime = true) => {
    const d = new Date(ms);
    const p = (n) => String(n).padStart(2, '0');
    const date = `${p(d.getUTCDate())}.${p(d.getUTCMonth() + 1)}.${d.getUTCFullYear()}`;
    return withTime ? `${date} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}` : date;
};

/** naivni ms -> 'YYYY-MM-DD HH:mm:ss' za API */
export const apiNaive = (ms) => {
    const d = new Date(ms);
    const p = (n) => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:00`;
};
